import {
    arrow,
    autoPlacement,
    autoUpdate,
    FloatingArrow,
    FloatingFocusManager,
    FloatingPortal,
    flip,
    Placement,
    ReferenceType,
    safePolygon,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useRole
} from "@floating-ui/react";
import { cn } from "@omni/utils/styles";
import React, { PropsWithChildren, useRef, useState } from "react";

type PopOverProps = PropsWithChildren<{
    dismissable?: boolean;
    title?: string;
    innerHTML?: string;
    onHover?: boolean;
    onClick?: boolean;
    placement?: Placement;
    anchorComponent: (
        setReference: (node: ReferenceType | null) => void,
        getReferenceProps: (
            userProps?: React.HTMLProps<Element>
        ) => Record<string, unknown>,
        popOverComponent: () => React.JSX.Element
    ) => React.ReactNode;
}>;

const PopOver = ({
    children,
    dismissable = false,
    onClick = true,
    onHover = false,
    placement,
    title,
    innerHTML,
    anchorComponent
}: PopOverProps) => {
    const [popoverOpen, setPopoverOpen] = useState(false);
    const closeTooltip = () => setPopoverOpen(false);

    const arrowRef = useRef(null);

    const middleware = [shift(), arrow({ element: arrowRef })];

    if (!placement) {
        middleware.unshift(flip());
        middleware.unshift(autoPlacement());
    }

    const { refs, floatingStyles, context } = useFloating({
        open: popoverOpen,
        placement,
        onOpenChange: setPopoverOpen,
        whileElementsMounted: autoUpdate,
        middleware
    });

    const focus = useFocus(context);
    const click = useClick(context, { enabled: onClick });
    const hover = useHover(context, {
        enabled: onHover,
        mouseOnly: true,
        handleClose: safePolygon()
    });
    const role = useRole(context);
    const dismiss = useDismiss(context);

    const { getFloatingProps, getReferenceProps } = useInteractions([
        click,
        hover,
        focus,
        role,
        dismiss
    ]);

    const popOverComponent = () => (
        <>
            {popoverOpen && (
                <FloatingPortal>
                    <FloatingFocusManager context={context}>
                        <div
                            role={"tooltip"}
                            ref={refs.setFloating}
                            style={floatingStyles}
                            className={cn(
                                "tw:max-w-150 tw:ml-2 tw:block tw:bg-white tw:bg-clip-padding tw:shadow-lg tw:rounded",
                                "tw:layer-high-alert/[0] tw:border tw:border-border tw:outline-none"
                            )}
                            {...getFloatingProps()}
                        >
                            {dismissable && (
                                <span
                                    className={cn(
                                        "tw:absolute tw:right-2.5 tw:top-1 tw:text-3xl tw:hover:cursor-pointer"
                                    )}
                                >
                                    <i
                                        className={"las la-times"}
                                        onClick={() => closeTooltip()}
                                    />
                                </span>
                            )}
                            {title && (
                                <h3 className={cn("tw:pl-2")}>{title}</h3>
                            )}
                            {children && (
                                <div className={cn("tw:p-2")}>{children}</div>
                            )}
                            {innerHTML && (
                                <div className={cn("tw:p-3.75")}>
                                    <div
                                        className={cn("tw:pr-3.75")}
                                        dangerouslySetInnerHTML={{
                                            __html: innerHTML
                                        }}
                                    />
                                </div>
                            )}

                            <FloatingArrow
                                ref={arrowRef}
                                context={context}
                                strokeWidth={1}
                                stroke="#e0e3eb"
                                fill="white"
                            />
                        </div>
                    </FloatingFocusManager>
                </FloatingPortal>
            )}
        </>
    );

    return (
        <>
            {anchorComponent(
                refs.setReference,
                getReferenceProps,
                popOverComponent
            )}
        </>
    );
};

export { PopOver };
